/**
 * Templates Page
 * Browse and manage statement templates with searchable dropdown
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { templatesAPI } from '../services/api';

interface Template {
  id: string;
  identifier: any;
  layoutType: string;
  paperSize: string;
  styleProfile?: {
    fontFamily: string;
    baseFontSize: number;
    primaryColor: string;
    secondaryColor: string;
  };
  previewImageUrl?: string;
  thumbnailPreviewSvg?: string;
  learnedFromSample?: boolean;
  createdAt: string;
}

export const Templates: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await templatesAPI.getTemplates();
      // Sort alphabetically by bank name
      const sorted = response.sort((a: any, b: any) => {
        const nameA = getBankDisplayName(a).toLowerCase();
        const nameB = getBankDisplayName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setTemplates(sorted);
    } catch (err: any) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const getBankDisplayName = (template: any): string => {
    if (!template.identifier) return 'Unknown Bank';
    if (typeof template.identifier === 'object' && !Array.isArray(template.identifier)) {
      return template.identifier.bankDisplayName || template.identifier.userFriendlyName || 'Unknown Bank';
    }
    if (Array.isArray(template.identifier)) {
      return template.identifier[0] || 'Unknown Bank';
    }
    return 'Unknown Bank';
  };

  const getBankCode = (template: any): string | null => {
    if (!template.identifier) return null;
    if (typeof template.identifier === 'object' && !Array.isArray(template.identifier)) {
      return template.identifier.bankCode || null;
    }
    if (Array.isArray(template.identifier) && template.identifier.length > 1) {
      return template.identifier[1];
    }
    return null;
  };

  // Filter templates based on search query
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(t => {
      const bankName = getBankDisplayName(t).toLowerCase();
      const bankCode = getBankCode(t)?.toLowerCase() || '';
      return bankName.includes(query) || bankCode.includes(query);
    });
  }, [templates, searchQuery]);

  const handleSelectTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setSearchQuery(getBankDisplayName(template));
    setIsDropdownOpen(false);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    try {
      await templatesAPI.deleteTemplate(templateId);
      setTemplates(templates.filter(t => t.id !== templateId));
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
        setSearchQuery('');
      }
    } catch (err: any) {
      alert(err.message || 'Failed to delete template');
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading templates...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button onClick={loadTemplates} className="mt-2 text-red-600 hover:text-red-800 underline">
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Bank Templates</h1>
        <p className="text-gray-600">
          Select a bank template to view details. {templates.length} templates available.
        </p>
      </div>

      {/* Search Dropdown */}
      <div className="mb-8" ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Search and Select Bank
        </label>
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsDropdownOpen(true);
              if (!e.target.value) setSelectedTemplate(null);
            }}
            onFocus={() => setIsDropdownOpen(true)}
            placeholder="Type to search banks..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Dropdown List */}
          {isDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
              {filteredTemplates.length === 0 ? (
                <div className="px-4 py-3 text-gray-500">No matching templates found</div>
              ) : (
                filteredTemplates.map((template) => {
                  const bankName = getBankDisplayName(template);
                  const bankCode = getBankCode(template);
                  return (
                    <div
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className={`px-4 py-3 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-0 ${
                        selectedTemplate?.id === template.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium text-gray-900">{bankName}</div>
                          <div className="text-sm text-gray-500">
                            {bankCode && <span className="mr-3">Code: {bankCode}</span>}
                            <span>{template.layoutType.replace(/_/g, ' ')}</span>
                          </div>
                        </div>
                        {template.styleProfile?.primaryColor && (
                          <div
                            className="w-6 h-6 rounded border border-gray-300"
                            style={{ backgroundColor: template.styleProfile.primaryColor }}
                            title={template.styleProfile.primaryColor}
                          ></div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Selected Template Details */}
      {selectedTemplate && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                {getBankDisplayName(selectedTemplate)}
              </h2>
              <div className="flex gap-2">
                {selectedTemplate.learnedFromSample && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                    AI Learned
                  </span>
                )}
                <button
                  onClick={() => handleDeleteTemplate(selectedTemplate.id)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Template Info */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 border-b pb-2">Template Details</h3>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Bank Code:</span>
                    <p className="font-medium">{getBankCode(selectedTemplate) || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Layout Type:</span>
                    <p className="font-medium">{selectedTemplate.layoutType.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Paper Size:</span>
                    <p className="font-medium">{selectedTemplate.paperSize.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Created:</span>
                    <p className="font-medium">{new Date(selectedTemplate.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>

                {selectedTemplate.styleProfile && (
                  <>
                    <h3 className="font-medium text-gray-900 border-b pb-2 mt-6">Style Profile</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Font:</span>
                        <p className="font-medium">{selectedTemplate.styleProfile.fontFamily}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Font Size:</span>
                        <p className="font-medium">{selectedTemplate.styleProfile.baseFontSize}pt</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Primary Color:</span>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-5 h-5 rounded border border-gray-300"
                            style={{ backgroundColor: selectedTemplate.styleProfile.primaryColor }}
                          ></div>
                          <span className="font-medium">{selectedTemplate.styleProfile.primaryColor}</span>
                        </div>
                      </div>
                      {selectedTemplate.styleProfile.secondaryColor && (
                        <div>
                          <span className="text-gray-500">Secondary Color:</span>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-5 h-5 rounded border border-gray-300"
                              style={{ backgroundColor: selectedTemplate.styleProfile.secondaryColor }}
                            ></div>
                            <span className="font-medium">{selectedTemplate.styleProfile.secondaryColor}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Preview */}
              <div>
                <h3 className="font-medium text-gray-900 border-b pb-2 mb-4">Preview</h3>
                {selectedTemplate.previewImageUrl ? (
                  <img
                    src={selectedTemplate.previewImageUrl}
                    alt={`${getBankDisplayName(selectedTemplate)} preview`}
                    className="border border-gray-200 rounded bg-white w-full"
                  />
                ) : selectedTemplate.thumbnailPreviewSvg ? (
                  <div
                    className="border border-gray-200 rounded bg-white p-2"
                    dangerouslySetInnerHTML={{ __html: selectedTemplate.thumbnailPreviewSvg }}
                  />
                ) : (
                  <div className="border border-gray-200 rounded bg-gray-50 p-8 text-center text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="mt-2">No preview available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 pt-6 border-t border-gray-200 flex gap-4">
              <button
                onClick={() => navigate(`/generate?templateId=${selectedTemplate.id}`)}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-center"
              >
                Generate Statement with This Template
              </button>
              <button
                onClick={() => {
                  setSelectedTemplate(null);
                  setSearchQuery('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      {!selectedTemplate && (
        <div className="grid grid-cols-3 gap-4 mt-8">
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">{templates.length}</div>
            <div className="text-sm text-gray-600">Total Templates</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-green-600">
              {templates.filter(t => t.learnedFromSample).length}
            </div>
            <div className="text-sm text-gray-600">AI Learned</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-purple-600">
              {new Set(templates.map(t => t.layoutType)).size}
            </div>
            <div className="text-sm text-gray-600">Layout Types</div>
          </div>
        </div>
      )}
    </div>
  );
};
